import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { RemoteConfig } from '@nx-customer-apps/shared/interfaces';
import { EnvironmentService } from './environment.service';
import { RemoteConfigParameter } from '@nx-customer-apps/shared/enums';

@Injectable({
    providedIn: 'root'
})
export class RemoteConfigService {
    private http = inject(HttpClient);
    private environmentService = inject(EnvironmentService);

    private readonly defaultAppConfig = this.environmentService.defaultAppConfig;

    private readonly configurationState = new BehaviorSubject({
        config: this.defaultAppConfig,
        loaded: false
    });

    /**
     * Get remote configuration for ViGuide Planning
     */
    public getConfiguration(): void {
        this.http
            .get<RemoteConfig>(this.environmentService.remoteConfigUrl, {
                params: { appId: this.environmentService.remoteConfigAppId }
            })
            .subscribe({
                next: config => {
                    this.configurationState.next({ config: this.parse(config), loaded: true });
                },
                error: err => {
                    this.configurationState.next({ config: this.defaultAppConfig, loaded: true });
                }
            });
    }

    /**
     * Get remote configuration state
     */
    public get state$(): Observable<{ config: RemoteConfig; loaded: boolean }> {
        return this.configurationState.asObservable();
    }

    /**
     * Get remote configuration value
     */
    public get config(): RemoteConfig {
        return this.configurationState.getValue().config;
    }

    public getConfigParameter(parameter: RemoteConfigParameter): string | number | boolean | undefined {
        return this.config[parameter];
    }

    public getConfigParameter$<T = string | number | boolean>(parameter: RemoteConfigParameter): Observable<T> {
        return this.state$.pipe(map(state => state.config[parameter] as T));
    }

    public getSplitedConfigParameter(parameter: RemoteConfigParameter): string[] {
        const configValue = this.config[parameter];
        if (typeof configValue === 'string' && configValue.length > 0) {
            return configValue.split(',').map(value => value.trim());
        }
        return [];
    }

    /**
     * Check if remote config is enabled. If not, use default configuration from environment.
     * Check if remote configuration flag is available. If not, use default configuration from environment.
     * @param remoteConfig
     */
    private parse(config: RemoteConfig): RemoteConfig {
        if (!this.environmentService.remoteConfigEnabled) {
            return this.defaultAppConfig;
        }

        for (const featureFlag in this.defaultAppConfig) {
            if (config[featureFlag] === undefined) {
                config[featureFlag] = this.defaultAppConfig[featureFlag];
            }
        }

        return config;
    }
}
